import React from "react";
import { Heading, User, Mail, Phone, MapPin, Type, AlignLeft, ChevronDownSquare, Calendar, CheckSquare, TextSelect, Link2 } from "lucide-react";

// Palette of draggable form elements (lead-focused set). Each auto-maps to a
// customer field where it makes sense.
export const FIELD_DEFS = [
  { type: "heading", label: "Heading", icon: Heading },
  { type: "fullName", label: "Full Name", icon: User },
  { type: "email", label: "Email", icon: Mail },
  { type: "phone", label: "Phone", icon: Phone },
  { type: "address", label: "Address", icon: MapPin },
  { type: "shortText", label: "Short Text", icon: Type },
  { type: "longText", label: "Long Text", icon: AlignLeft },
  { type: "dropdown", label: "Dropdown", icon: ChevronDownSquare },
  { type: "date", label: "Date", icon: Calendar },
  { type: "checkbox", label: "Checkbox", icon: CheckSquare },
  { type: "textBlock", label: "Text", icon: TextSelect },
  { type: "hyperlink", label: "Hyperlink", icon: Link2 },
];

// Display-only elements — never collect a submission value, so they're
// exempt from "required"/"maps to customer field"/placeholder controls.
export const DISPLAY_ONLY_TYPES = new Set(["heading", "textBlock", "hyperlink"]);

export const MAP_OPTIONS = [
  ["none", "No mapping"], ["name", "Full name"], ["firstName", "First name"], ["lastName", "Last name"],
  ["email", "Email"], ["phone", "Phone"], ["address", "Address"], ["city", "City"],
  ["state", "State"], ["zip", "Zip"], ["service", "Service"], ["message", "Message"],
];

// Element types whose customer mapping is implied (no map selector shown).
export const FIXED_MAP_TYPES = new Set(["heading", "fullName", "address"]);
export const NO_PLACEHOLDER_TYPES = new Set(["heading", "fullName", "address", "checkbox", "date", "dropdown", "textBlock", "hyperlink"]);

export const THEMES = [
  { id: "classic", name: "Classic", desc: "Accent top bar" },
  { id: "bold", name: "Bold", desc: "Solid accent header" },
  { id: "minimal", name: "Minimal", desc: "Clean, no bar" },
];

export const COND_OPS = [
  ["equals", "is"],
  ["not_equals", "is not"],
  ["filled", "is filled in"],
  ["not_filled", "is empty"],
];

// Returns header layout styling for a given theme + accent.
export function headerStyle(theme, accent) {
  if (theme === "bold") return { wrap: { backgroundColor: accent }, title: "text-white", intro: "text-white/80" };
  if (theme === "minimal") return { wrap: {}, title: "text-slate-900", intro: "text-slate-500" };
  return { wrap: { borderTop: `5px solid ${accent}` }, title: "text-slate-900", intro: "text-slate-500" };
}

export function flattenVal(v) {
  if (v && typeof v === "object") return Object.values(v).filter(Boolean).join(" ");
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return v == null ? "" : String(v);
}

// Evaluate a field's conditional-logic rule against current form values.
export function fieldVisible(field, values) {
  const c = field.condition;
  if (!c || !c.fieldId) return true;
  const s = flattenVal(values[c.fieldId]).trim().toLowerCase();
  const t = (c.value || "").trim().toLowerCase();
  switch (c.op) {
    case "filled": return s !== "";
    case "not_filled": return s === "";
    case "not_equals": return s !== t;
    default: return s === t;
  }
}

const uid = () => `fld-${Math.random().toString(36).slice(2, 10)}`;

export function makeField(type) {
  const base = { id: uid(), type, label: "", placeholder: "", required: false, map: "none", options: [], condition: null };
  switch (type) {
    case "heading": return { ...base, label: "Section heading" };
    case "fullName": return { ...base, label: "Full name", required: true, map: "name" };
    case "email": return { ...base, label: "Email", placeholder: "you@email.com", required: true, map: "email" };
    case "phone": return { ...base, label: "Phone", placeholder: "(555) 123-4567", map: "phone" };
    case "address": return { ...base, label: "Address", map: "address" };
    case "shortText": return { ...base, label: "Short text", placeholder: "" };
    case "longText": return { ...base, label: "How can we help?", placeholder: "Tell us about the job…", map: "message" };
    case "dropdown": return { ...base, label: "Dropdown", options: ["Option 1", "Option 2"] };
    case "date": return { ...base, label: "Date" };
    case "checkbox": return { ...base, label: "Checkbox", placeholder: "I agree" };
    case "textBlock": return { ...base, label: "Text", content: "Add your text here.", fontSize: 14, color: "#334155" };
    case "hyperlink": return { ...base, label: "Link text", url: "https://" };
    default: return { ...base, label: "Field" };
  }
}

// Normalize legacy field types (from the earlier builder) into the new set.
export function normalizeField(f) {
  const legacy = { text: "shortText", textarea: "longText", select: "dropdown", number: "shortText" };
  return { ...f, type: legacy[f.type] || f.type };
}

export function fieldDef(type) {
  return FIELD_DEFS.find((d) => d.type === type) || FIELD_DEFS[0];
}

/**
 * Renders a display-only block (heading / text / hyperlink) — no input,
 * no submitted value. Shared by the builder canvas and the public form.
 */
export function FieldDisplayBlock({ field, accent = "#2563EB" }) {
  if (field.type === "textBlock") {
    return (
      <p
        data-testid={`pf-field-${field.id}`}
        className="whitespace-pre-wrap leading-relaxed"
        style={{ fontSize: `${field.fontSize || 14}px`, color: field.color || "#334155" }}
      >
        {field.content || ""}
      </p>
    );
  }
  if (field.type === "hyperlink") {
    return (
      <a
        href={field.url || "#"}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`pf-field-${field.id}`}
        className="text-[14px] font-semibold underline underline-offset-2 hover:opacity-80 transition-opacity"
        style={{ color: accent }}
      >
        {field.label || "Link"}
      </a>
    );
  }
  return <h3 className="text-[18px] font-bold text-slate-900">{field.label || "Section heading"}</h3>;
}

/**
 * Renders the actual input control for a field. Shared by the public form
 * (interactive) and the builder canvas (disabled preview).
 */
export function FieldControl({ field, value, onChange, disabled, accent = "#2563EB", inputTextColor }) {
  const base = "w-full px-3 py-2.5 rounded-lg border border-slate-200 text-[14px] focus:outline-none focus:ring-2 transition-shadow disabled:bg-slate-50 disabled:text-slate-400";
  const style = { "--tw-ring-color": `${accent}33`, ...(inputTextColor ? { color: inputTextColor } : {}) };
  const set = (v) => onChange && onChange(v);
  const tid = `pf-field-${field.id}`;
  const v = value;

  switch (field.type) {
    case "heading":
      return null;
    case "fullName": {
      const d = v && typeof v === "object" ? v : {};
      return (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <input disabled={disabled} className={base} style={style} placeholder="First" value={d.first || ""} onChange={(e) => set({ ...d, first: e.target.value })} data-testid={`${tid}-first`} />
            <div className="text-[11px] text-slate-400 mt-1">First Name</div>
          </div>
          <div>
            <input disabled={disabled} className={base} style={style} placeholder="Last" value={d.last || ""} onChange={(e) => set({ ...d, last: e.target.value })} data-testid={`${tid}-last`} />
            <div className="text-[11px] text-slate-400 mt-1">Last Name</div>
          </div>
        </div>
      );
    }
    case "address": {
      const d = v && typeof v === "object" ? v : {};
      return (
        <div className="space-y-2">
          <input disabled={disabled} className={base} style={style} placeholder="Street address" value={d.street || ""} onChange={(e) => set({ ...d, street: e.target.value })} data-testid={`${tid}-street`} />
          <div className="grid grid-cols-3 gap-2">
            <input disabled={disabled} className={base} style={style} placeholder="City" value={d.city || ""} onChange={(e) => set({ ...d, city: e.target.value })} data-testid={`${tid}-city`} />
            <input disabled={disabled} className={base} style={style} placeholder="State" value={d.state || ""} onChange={(e) => set({ ...d, state: e.target.value })} data-testid={`${tid}-state`} />
            <input disabled={disabled} className={base} style={style} placeholder="Zip" value={d.zip || ""} onChange={(e) => set({ ...d, zip: e.target.value })} data-testid={`${tid}-zip`} />
          </div>
        </div>
      );
    }
    case "longText":
    case "paragraph":
    case "textarea":
      return <textarea disabled={disabled} rows={4} className={base} style={style} placeholder={field.placeholder || ""} value={v || ""} onChange={(e) => set(e.target.value)} data-testid={tid} />;
    case "dropdown":
    case "select":
      return (
        <select disabled={disabled} className={`${base} bg-white fb-form-field-select`} style={style} value={v || ""} onChange={(e) => set(e.target.value)} data-testid={tid}>
          <option value="">Select…</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    case "checkbox":
      return (
        <label className="flex items-center gap-2 text-[14px] text-slate-700">
          <input type="checkbox" disabled={disabled} checked={!!v} onChange={(e) => set(e.target.checked)} style={{ accentColor: accent }} className="h-4 w-4" data-testid={tid} />
          {field.placeholder || field.label}
        </label>
      );
    case "date":
      return <input type="date" disabled={disabled} className={`${base} fb-form-field-date`} style={style} value={v || ""} onChange={(e) => set(e.target.value)} data-testid={tid} />;
    case "email":
      return <input type="email" disabled={disabled} className={base} style={style} placeholder={field.placeholder || ""} value={v || ""} onChange={(e) => set(e.target.value)} data-testid={tid} />;
    case "phone":
      return <input type="tel" disabled={disabled} className={base} style={style} placeholder={field.placeholder || ""} value={v || ""} onChange={(e) => set(e.target.value)} data-testid={tid} />;
    default:
      return <input type="text" disabled={disabled} className={base} style={style} placeholder={field.placeholder || ""} value={v || ""} onChange={(e) => set(e.target.value)} data-testid={tid} />;
  }
}
